import React from 'react';
import { gql } from '@apollo/client';
import { useMutation } from '@pv/common/hooks';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Alert,
  Stack,
  Link,
  Box,
  Button,
} from '@mui/material';
import { PvDialog } from '@pv/common/components';
import { enqueueSnackbar } from 'notistack';

const approveVenueDataImportMutation = gql`
  mutation ApproveVenueDataImport($input: ApproveVenueDataImportInput!) {
    approveVenueDataImport(input: $input) {
      venueDataImport {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

interface StagingFile {
  name: string;
  size_bytes: number;
  detected_type: string;
}

interface StagingSummary {
  created_events?: number;
  updated_events?: number;
  created_planners?: number;
  updated_planners?: number;
  created_spaces?: number;
  updated_spaces?: number;
  created_accounts?: number;
  created_payments?: number;
  updated_payments?: number;
  matched_documents?: number;
  unmatched_documents?: number;
  events_by_status?: Record<string, number>;
}

interface StagingError {
  message: string;
  fatal?: boolean;
}

interface StagingResultsDialogProps {
  open: boolean;
  onClose: () => void;
  importId?: string;
  status?: string;
  stagingResults: {
    files?: StagingFile[];
    errors?: StagingError[];
    fatal_errors?: StagingError[];
    summary?: StagingSummary;
    rooms?: string[];
  } | null | undefined;
  storageUrl?: string | null;
  createdAt?: string;
  onRollback?: () => void;
}

const FILE_TYPE_LABELS: Record<string, string> = {
  ts_leads: 'Tripleseat Leads',
  ts_event_details_report: 'Tripleseat Event Details Report',
  ts_accounts_report: 'Tripleseat Accounts Report',
  ts_documents_zip: 'Tripleseat Documents',
  ts_payments_report: 'Tripleseat Payments Report',
  event_io_events: 'Event IO Events',
};

const STAGING_SUMMARY_LABELS: { key: keyof StagingSummary; label: string }[] = [
  { key: 'created_events', label: 'Events to create' },
  { key: 'updated_events', label: 'Events to update' },
  { key: 'created_planners', label: 'Planners to create' },
  { key: 'updated_planners', label: 'Planners to update' },
  { key: 'created_accounts', label: 'Accounts to create' },
  { key: 'created_spaces', label: 'Spaces to create' },
  { key: 'updated_spaces', label: 'Spaces to update' },
  { key: 'created_payments', label: 'Payments to create' },
  { key: 'updated_payments', label: 'Payments to update' },
  { key: 'matched_documents', label: 'Documents to import' },
  { key: 'unmatched_documents', label: 'Documents without matching events' },
];

const IMPORT_SUMMARY_LABELS: { key: keyof StagingSummary; label: string }[] = [
  { key: 'created_events', label: 'Events created' },
  { key: 'updated_events', label: 'Events updated' },
  { key: 'created_planners', label: 'Planners created' },
  { key: 'updated_planners', label: 'Planners updated' },
  { key: 'created_accounts', label: 'Accounts created' },
  { key: 'created_spaces', label: 'Spaces created' },
  { key: 'updated_spaces', label: 'Spaces updated' },
  { key: 'created_payments', label: 'Payments created' },
  { key: 'updated_payments', label: 'Payments updated' },
  { key: 'matched_documents', label: 'Documents imported' },
  { key: 'unmatched_documents', label: 'Documents skipped (no matching event)' },
];

const formatFileType = (type: string) => FILE_TYPE_LABELS[type] ?? type;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatStatusLabel = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const StagingResultsDialog = ({
  open,
  onClose,
  importId,
  status,
  stagingResults,
  storageUrl,
  createdAt,
  onRollback,
}: StagingResultsDialogProps) => {
  const isImportMode = status === 'completed';
  const files = stagingResults?.files ?? [];
  const errors = stagingResults?.errors ?? [];
  const fatalErrors = stagingResults?.fatal_errors ?? [];
  const summary = stagingResults?.summary;
  const rooms = stagingResults?.rooms ?? [];
  const eventsByStatus = summary?.events_by_status ?? {};

  const summaryLabels = isImportMode ? IMPORT_SUMMARY_LABELS : STAGING_SUMMARY_LABELS;
  const summaryItems = summaryLabels.filter(
    ({ key }) => summary && (summary[key] as number | undefined)
  );

  const canImport = status === 'staged' && !!importId;

  const [approveImport, { loading: approving }] = useMutation(
    approveVenueDataImportMutation,
    {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('Import started', { variant: 'success' });
        window.location.reload();
      },
      onSomeErrorsCompleted: (resp: any) => {
        resp.approveVenueDataImport.errors.forEach((e: { message: string }) => {
          enqueueSnackbar(`Error: ${e.message}`, { variant: 'error' });
        });
      },
    }
  );

  const handleImport = () => {
    approveImport({ variables: { input: { id: importId } } });
  };

  return (
    <PvDialog
      open={open}
      onClose={onClose}
      title={isImportMode ? 'Import Results' : 'Staging Results'}
      onConfirm={handleImport}
      confirmButtonText="Import"
      confirmDisabled={!canImport}
      hideConfirmButton={!canImport}
      loading={approving}
      maxWidth="md"
    >
      <Stack spacing={2}>
        {fatalErrors.length > 0 && (
          <Stack spacing={1}>
            {fatalErrors.map((error, i) => (
              <Alert key={i} severity="error">
                {error.message}
              </Alert>
            ))}
          </Stack>
        )}
        {errors.length > 0 && (
          <Stack spacing={1}>
            {errors.map((error, i) => (
              <Alert key={i} severity="warning">
                {error.message}
              </Alert>
            ))}
          </Stack>
        )}
        {storageUrl && (
          <Typography variant="body2" color="text.secondary">
            From{' '}
            <Link href={storageUrl} target="_blank" rel="noreferrer">
              {storageUrl}
            </Link>
          </Typography>
        )}
        {createdAt && (
          <Typography variant="body2" color="text.secondary">
            Created at {formatDate(createdAt)}
          </Typography>
        )}
        {summaryItems.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="overline">Import Preview</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {summaryItems.map(({ key, label }) => (
                <Chip
                  key={key}
                  label={`${summary![key]} ${label}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Stack>
        )}
        {Object.keys(eventsByStatus).length > 0 && (
          <Stack spacing={1}>
            <Typography variant="overline">Events by Status</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(eventsByStatus).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${count} ${formatStatusLabel(status)}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Stack>
        )}
        {rooms.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="overline">Rooms Detected</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rooms.map((room) => (
                <Chip key={room} label={room} size="small" variant="outlined" />
              ))}
            </Box>
          </Stack>
        )}
        {files.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="overline">Files</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead
                  sx={{
                    backgroundColor: 'rgba(34, 34, 34, 0.02)',
                  }}
                >
                  <TableRow>
                    {['File Name', 'Size', 'Detected Type'].map((col) => (
                      <TableCell key={col}>
                        <Typography variant="overline">{col}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.name}>
                      <TableCell>
                        <Typography variant="body2">{file.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatBytes(file.size_bytes)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatFileType(file.detected_type)}
                          size="small"
                          color={
                            file.detected_type === 'unknown' ? 'default' : 'success'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
        {isImportMode && onRollback && (
          <Box>
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={() => {
                onRollback();
                onClose();
              }}
            >
              Rollback Import
            </Button>
          </Box>
        )}
      </Stack>
    </PvDialog>
  );
};
